/**
 * PitchPrint Cart Auto-Edit Handler
 * Automatically clicks preview image when redirected from edit button
 *
 * This script runs on the cart page and checks if the user was redirected
 * here to edit a specific project (via ?pp_edit=projectId URL parameter).
 * If so, it automatically clicks the corresponding preview image to trigger
 * PitchPrint's native edit flow.
 *
 * Benefits of URL parameter approach:
 * - Works across devices (shareable link)
 * - Works across browsers
 * - Persists in browser history (can use back button)
 * - More reliable than sessionStorage
 */

(function() {
  const PREFIX = '[PP-Cart-Auto-Edit]';
  const DEBUG = true;

  const log = (...args) => {
    if (DEBUG) console.log(PREFIX, ...args);
  };

  const init = () => {
    log('🔍 Checking for edit redirect...');

    // Check if we were redirected here to edit a project via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const projectIdToEdit = urlParams.get('pp_edit');

    if (!projectIdToEdit) {
      log('ℹ️ No edit redirect detected (no pp_edit parameter in URL)');
      return;
    }

    log('✅ Edit redirect detected for project:', projectIdToEdit);

    // Wait for PitchPrint preview images to load BEFORE cleaning URL
    // We need to give the cart page time to render the PitchPrint previews
    const maxAttempts = 30; // Try for up to 6 seconds (30 * 200ms) - increased wait time
    let attempts = 0;
    let urlCleaned = false;

    const findAndClickPreview = () => {
      attempts++;
      log(`🔍 Attempt ${attempts}/${maxAttempts} to find preview image...`);

      // First, check if PitchPrint is even loaded
      if (attempts === 1) {
        log('🔍 Checking PitchPrint status...');
        log('  window.ppclient exists:', !!window.ppclient);
        log('  window.PitchPrint exists:', !!window.PitchPrint);

        // Check for all images with pitchprint in src
        const allPPImages = document.querySelectorAll('img[src*="pitchprint"]');
        log(`  Found ${allPPImages.length} PitchPrint images on page`);

        // Check for cart items with properties
        const cartItems = document.querySelectorAll('.cart-item, [data-cart-item]');
        log(`  Found ${cartItems.length} cart items on page`);
      }

      // Try multiple selector patterns to find the preview image
      // IMPORTANT: All selectors must be image-specific to avoid matching edit buttons
      const selectors = [
        `img[id="p_${projectIdToEdit}"]`,                    // ID with p_ prefix
        `img[src*="${projectIdToEdit}"]`,                     // Any img with projectId in src
        `img[onclick*="${projectIdToEdit}"]`,                 // Any img with projectId in onclick
        `a[onclick*="${projectIdToEdit}"] img`,               // img inside anchor with projectId
        `img[data-project-id="${projectIdToEdit}"]`,          // img with data-project-id attribute
        `a[data-project-id="${projectIdToEdit}"] img`,        // img inside anchor with data-project-id
        `img[data-pp-project="${projectIdToEdit}"]`,          // img with alternative data attribute
        `[data-pp-project="${projectIdToEdit}"] img`          // img inside element with data attribute
      ];

      let previewElement = null;

      for (const selector of selectors) {
        previewElement = document.querySelector(selector);
        if (previewElement) {
          log(`✅ Found preview with selector: ${selector}`);
          log('📸 Preview element:', previewElement);
          break;
        }
      }

      // Additional debugging on first few attempts
      if (attempts <= 3) {
        log('🔍 All images with "pitchprint" in src:');
        document.querySelectorAll('img[src*="pitchprint"], img[src*="s3-eu-west-1"]').forEach((img, i) => {
          log(`  ${i + 1}. ${img.src} (id: ${img.id || 'none'})`);
        });
      }

      if (previewElement) {
        log('🎯 Preview found! Waiting 500ms before clicking...');

        // Clean up the URL now that we found the preview
        if (!urlCleaned) {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, '', cleanUrl);
          log('🗑️ Cleaned URL parameter');
          urlCleaned = true;
        }

        // Wait a moment to ensure everything is ready
        setTimeout(() => {
          log('🖱️ Clicking preview image to open editor...');

          // Try clicking the image itself
          previewElement.click();

          // If image is inside a clickable parent, try that too
          const clickableParent = previewElement.closest('a[onclick]');
          if (clickableParent) {
            log('🖱️ Also clicking parent anchor element...');
            clickableParent.click();
          }

          log('✅ Click triggered! Editor should open with saved project');
        }, 500);

        return; // Success! Stop trying
      }

      // Not found yet - try again if we haven't exceeded max attempts
      if (attempts < maxAttempts) {
        // On attempt 5, try to manually trigger PitchPrint preview rendering
        if (attempts === 5) {
          log('⚡ Attempting to manually trigger PitchPrint preview rendering...');
          tryManualPreviewTrigger();
        }

        log(`⏳ Preview not found yet, retrying in 200ms...`);
        setTimeout(findAndClickPreview, 200);
      } else {
        log('❌ Failed to find preview image after', maxAttempts, 'attempts');
        log('⚠️ Falling back to direct API call...');

        // FALLBACK: Try to directly call PitchPrint API
        tryDirectEdit();
      }
    };

    // Fallback function to manually trigger preview rendering
    const tryManualPreviewTrigger = () => {
      log('🔧 Checking if PitchPrint _updatePreviews function exists...');

      if (window.ppclient && typeof window.ppclient._updatePreviews === 'function') {
        log('✅ Found _updatePreviews, attempting to call it...');
        try {
          window.ppclient._updatePreviews();
          log('✅ Called _updatePreviews successfully');
        } catch (e) {
          log('⚠️ Error calling _updatePreviews:', e.message);
        }
      } else {
        log('⚠️ _updatePreviews not available');
      }

      // Also try triggering Shopify cart render event
      if (window.Shopify && window.Shopify.CartJS) {
        log('🔧 Triggering Shopify cart render...');
        document.dispatchEvent(new CustomEvent('cart:updated'));
      }
    };

    // Final fallback - try direct edit API call
    const tryDirectEdit = () => {
      log('🎯 Attempting direct edit via PitchPrint API as last resort...');

      if (!window.ppclient) {
        console.error('PitchPrint: Client not available. Please click the preview image to edit.');
        return;
      }

      try {
        // Get designId from _vars
        const designId = window.ppclient._vars?.designId;

        if (!designId) {
          log('❌ No designId found in ppclient._vars');
          console.error('PitchPrint: Unable to determine design template. Please click the preview image to edit.');
          return;
        }

        log('✅ Found designId:', designId);
        log('🚀 Setting _vars and calling showApp()...');

        // Set variables
        window.ppclient._vars.projectId = projectIdToEdit;
        window.ppclient._vars.designId = designId;
        window.ppclient._vars.mode = 'edit';

        // Call showApp
        window.ppclient.showApp();

        log('✅ Direct edit API called - editor should open');
        log('⚠️ Note: This may load a default design instead of saved project');
        log('⚠️ If this happens, please contact PitchPrint support for correct API');
      } catch (e) {
        console.error('PitchPrint: Error attempting direct edit:', e);
        console.error('Please click the preview image to edit your design.');
      }
    };

    // Start looking for the preview image
    findAndClickPreview();
  };

  // Wait for page to be fully loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  log('✅ Auto-edit handler initialized');
})();
